import { useEffect, useMemo, useState } from 'react';
import type { CartProvider, CatalogProvider, PricingProvider, QuoteResult } from '@ox/schema';
import { useEditorStore } from '../store/editor-store';
import { getUsedViewMetrics } from '../lib/viewMetrics';
import { SizeGuideModal } from './SizeGuideModal';
import { PriceBreakdownSheet } from './PriceBreakdownSheet';
import { chooseSizeCtaLabel, modelShowsSizes, purchasableColorKey } from '../lib/modelAxes';
import { printServiceQuoteFields, modelQuoteFields } from '../lib/printServiceQuote';
import { PrintServiceOptions } from './PrintServiceOptions';
import { PrintMethodOptions } from './PrintMethodOptions';

const DEFAULT_PRINT_GARMENT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];
const OTHER_PRINT_GARMENT_SIZE = '__other__';

function isSyntheticGarmentSize(label: string): boolean {
  const slug = label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
  return slug === 'u' || slug === 'unica';
}

function formatMoney(currency: string, amount: number): string {
  return `${currency} ${amount.toFixed(2)}`;
}

export function SizeQtyModal({
  pricing,
  cart,
  catalog,
  designId,
  versionId,
  onClose,
  onAdded,
  sheetMode = false,
}: {
  pricing: PricingProvider;
  cart: CartProvider;
  catalog: CatalogProvider;
  designId: string;
  versionId: string;
  onClose: () => void;
  onAdded: (result: { cartCount: number; articleCount: number; redirectUrl?: string }) => void;
  sheetMode?: boolean;
}) {
  const model = useEditorStore((s) => s.model);
  const document = useEditorStore((s) => s.document);
  const colorKey = useEditorStore((s) => s.document.selection.colorKey);
  const colorHex = useEditorStore((s) => s.document.selection.colorHex);
  // Purchasable Woo color. A free hex is matched to the catalog swatch with that hex.
  const buyColorKey = useMemo(
    () => purchasableColorKey(model, colorKey, colorHex),
    [model, colorKey, colorHex],
  );
  const [qty, setQty] = useState<Record<string, number>>({});
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [totalLabel, setTotalLabel] = useState('Total: 0.00');
  const [note, setNote] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [printGarmentSize, setPrintGarmentSize] = useState('');
  const [customPrintGarmentSize, setCustomPrintGarmentSize] = useState('');
  const [printComment, setPrintComment] = useState('');
  const sizeGuideUrl = model?.sizeGuideUrl?.trim();
  const hasSizeGuide = !!sizeGuideUrl && sizeGuideUrl !== '#';
  const sheetTitle = chooseSizeCtaLabel(model, false).replace(/^🛒\s*/, '');
  const qtyOnly = !modelShowsSizes(model);
  const askBringOwnGarment = !!(model?.printService && model?.askGarmentColor);

  const printGarmentSizeOptions = useMemo(() => {
    const fromModel = (model?.printGarmentSizes ?? [])
      .map((s) => s.trim())
      .filter((s) => s && !isSyntheticGarmentSize(s));
    if (fromModel.length) return fromModel;
    if (model && modelShowsSizes(model)) {
      const fromSizes = model.sizes
        .map((s) => s.label)
        .filter((s) => s && !isSyntheticGarmentSize(s));
      if (fromSizes.length) return fromSizes;
    }
    return DEFAULT_PRINT_GARMENT_SIZES;
  }, [model]);
  const selectedPrintGarmentSize =
    printGarmentSize === OTHER_PRINT_GARMENT_SIZE
      ? customPrintGarmentSize.trim()
      : printGarmentSize.trim();

  const viewMetrics = useMemo(
    () => getUsedViewMetrics(document, model?.printAreas),
    [document, model?.printAreas],
  );

  const rows = useMemo(() => {
    if (!model) return [];
    return model.sizes
      .map((size) => {
        const variant = model.variants.find(
          (v) => v.colorKey === buyColorKey && v.sizeId === size.id,
        );
        return { size, variant };
      })
      .filter(({ variant }) => !!variant);
  }, [model, buyColorKey]);

  useEffect(() => {
    const lines = Object.entries(qty)
      .filter(([, q]) => q > 0)
      .map(([sizeId, quantity]) => {
        const variant = model?.variants.find(
          (v) => v.colorKey === buyColorKey && v.sizeId === sizeId,
        );
        return variant ? { variantId: variant.id, quantity } : null;
      })
      .filter(Boolean) as { variantId: string; quantity: number }[];

    if (!lines.length) {
      setQuote(null);
      setTotalLabel('Total: 0.00');
      setNote(undefined);
      return;
    }
    void pricing
      .quote({
        lines,
        designId,
        viewMetrics,
        ...printServiceQuoteFields(document.selection),
        ...modelQuoteFields(model),
      })
      .then((q) => {
        if (!q?.total) {
          setQuote(null);
          setTotalLabel('Total: —');
          return;
        }
        setQuote(q);
        setTotalLabel(`Total: ${formatMoney(q.total.currency, q.total.amount)}`);
        setNote(q.note ?? q.discounts?.[0]?.label);
      })
      .catch(() => {
        setQuote(null);
        setTotalLabel('Total: —');
      });
  }, [qty, pricing, model, buyColorKey, designId, viewMetrics, document.selection]);

  const articleCount = Object.values(qty).reduce((s, n) => s + n, 0);
  const colorLabel =
    model?.colors.find((c) => c.key === buyColorKey)?.label ?? buyColorKey;

  if (!model) return null;

  const body = (
    <>
      <PrintMethodOptions catalog={catalog} />
      <PrintServiceOptions />
      {askBringOwnGarment && (
        <div className="ox-print-garment-fields">
          <label className="ox-print-garment-field">
            <span>Talla de impresión</span>
            <select
              value={printGarmentSize}
              onChange={(e) => {
                setPrintGarmentSize(e.target.value);
                if (e.target.value !== OTHER_PRINT_GARMENT_SIZE) {
                  setCustomPrintGarmentSize('');
                }
              }}
              required
            >
              <option value="">Elegir talla…</option>
              {printGarmentSizeOptions.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
              <option value={OTHER_PRINT_GARMENT_SIZE}>Otra talla…</option>
            </select>
          </label>
          {printGarmentSize === OTHER_PRINT_GARMENT_SIZE && (
            <label className="ox-print-garment-field">
              <span>Indica la talla</span>
              <input
                type="text"
                value={customPrintGarmentSize}
                onChange={(e) => setCustomPrintGarmentSize(e.target.value)}
                placeholder="Ej. 5XL, 12 años, medida especial"
                maxLength={80}
                autoFocus
              />
            </label>
          )}
          <label className="ox-print-garment-field">
            <span>Comentario adicional</span>
            <textarea
              value={printComment}
              onChange={(e) => setPrintComment(e.target.value)}
              placeholder="Ej. marca en el cuello, manchas, ubicación…"
              rows={2}
              maxLength={500}
            />
          </label>
        </div>
      )}
      {hasSizeGuide && !qtyOnly && (
        <button type="button" className="ox-size-guide" onClick={() => setSizeGuideOpen(true)}>
          Guía de tallas
        </button>
      )}
      <div className="ox-size-rows">
        {rows.map(({ size, variant }) => {
          const disabled = !variant || variant.availability === 'out_of_stock';
          return (
            <div key={size.id} className={`ox-size-row ${disabled ? 'disabled' : ''}`}>
              <span>{qtyOnly ? 'Cantidad' : size.label}</span>
              {disabled ? (
                <span className="ox-agotado">
                  {variant?.availabilityLabel ?? 'Agotado temporalmente'}
                </span>
              ) : (
                <div className="ox-stepper">
                  <button
                    type="button"
                    onClick={() =>
                      setQty((q) => ({
                        ...q,
                        [size.id]: Math.max(0, (q[size.id] ?? 0) - 1),
                      }))
                    }
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={qty[size.id] ?? 0}
                    onChange={(e) =>
                      setQty((q) => ({
                        ...q,
                        [size.id]: Math.max(0, Number(e.target.value) || 0),
                      }))
                    }
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setQty((q) => ({
                        ...q,
                        [size.id]: (q[size.id] ?? 0) + 1,
                      }))
                    }
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <footer className="ox-size-footer">
        <div>
          <div>{articleCount} artículos</div>
          <button
            type="button"
            className="ox-price-breakdown-trigger"
            disabled={!quote}
            onClick={() => setBreakdownOpen(true)}
          >
            <strong>{totalLabel}</strong>
            {quote?.breakdown && quote.breakdown.length > 0 && (
              <span className="ox-price-chevron" aria-hidden>
                ›
              </span>
            )}
          </button>
          {note && <div className="ox-hint">{note}</div>}
        </div>
        <button
          type="button"
          className="ox-primary"
          disabled={!articleCount || busy}
          onClick={async () => {
            setBusy(true);
            try {
              if (
                model.printService &&
                model.askGarmentPrintType &&
                !document.selection.garmentPrintType
              ) {
                window.alert('Elige el tipo de prenda (sin o con base blanca) antes de comprar.');
                setBusy(false);
                return;
              }
              if (askBringOwnGarment && !selectedPrintGarmentSize) {
                window.alert('Indica la talla de la prenda donde va la impresión.');
                setBusy(false);
                return;
              }
              const lines = Object.entries(qty)
                .filter(([, q]) => q > 0)
                .map(([sizeId, quantity]) => {
                  const variant = model.variants.find(
                    (v) => v.colorKey === buyColorKey && v.sizeId === sizeId,
                  )!;
                  return { variantId: variant.id, quantity };
                });
              const bringOwnFields = askBringOwnGarment
                ? {
                    printGarmentSize: selectedPrintGarmentSize,
                    ...(printComment.trim()
                      ? { printComment: printComment.trim() }
                      : {}),
                  }
                : {};
              const res = await cart.addToCart({
                lines,
                designId,
                versionId,
                viewMetrics,
                ...printServiceQuoteFields(document.selection),
                ...modelQuoteFields(model),
                ...bringOwnFields,
              });
              onAdded({
                cartCount: res.cartCount,
                articleCount,
                redirectUrl: res.redirectUrl,
              });
              onClose();
            } catch (err) {
              window.alert(err instanceof Error ? err.message : 'No se pudo añadir al carrito');
            } finally {
              setBusy(false);
            }
          }}
        >
          Añadir a la cesta
        </button>
      </footer>
    </>
  );

  return (
    <>
      <div
        className={sheetMode ? 'ox-sheet-backdrop' : 'ox-modal-backdrop'}
        onClick={onClose}
      >
        <div
          className={sheetMode ? 'ox-sheet ox-sheet-tall ox-size-sheet' : 'ox-modal'}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label={sheetTitle}
        >
          <header className={sheetMode ? 'ox-sheet-header' : undefined}>
            {sheetMode ? (
              <>
                <span className="ox-sheet-title">{sheetTitle}</span>
                <button type="button" className="ox-sheet-x" onClick={onClose} aria-label="Cerrar">
                  ✕
                </button>
              </>
            ) : (
              <>
                <h2>{sheetTitle}</h2>
                <button type="button" onClick={onClose}>
                  ✕
                </button>
              </>
            )}
          </header>
          {sheetMode ? <div className="ox-sheet-body">{body}</div> : body}
        </div>
      </div>
      {sizeGuideOpen && hasSizeGuide && sizeGuideUrl && (
        <SizeGuideModal url={sizeGuideUrl} onClose={() => setSizeGuideOpen(false)} />
      )}
      {breakdownOpen && quote && (
        <PriceBreakdownSheet
          quote={quote}
          productName={model.name}
          colorLabel={colorLabel}
          articleCount={articleCount}
          onClose={() => setBreakdownOpen(false)}
          sheetMode={sheetMode}
        />
      )}
    </>
  );
}
